/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo } from 'react';

import { cssMap as cssMapCompiled } from '@compiled/react';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import SearchIcon from '@atlaskit/icon/core/search';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { FollowUpButton } from '@atlassian/conversation-assistant-ui-components/ui/follow-up-button';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { OpenChatButton } from '../../common/ui/open-chat/open-chat-button';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { useAIAnswerContext } from '../ai-answer-context';
import { onErrorStateSuggestedQueriesShown } from '../on-error-state-suggested-queries-shown';
import { onErrorStateSuggestedQueryClick } from '../on-error-state-suggested-query-click';
import { onFollowUpSuggestionClick } from '../on-follow-up-suggestion-click';

import { AIAnswerFollowUpPill } from './ai-answer-follow-up-pill';
import { AIAnswerFollowUpPillText } from './ai-answer-follow-up-pill-text';

const stylesCompiled = cssMapCompiled({
	pillLineHeight: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
		lineHeight: '16px',
	},
});

const styles = cssMap({
	button: {
		color: token('color.text.subtle'),
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flexShrink: 1,
		backgroundColor: token('color.background.neutral.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},

	queryText: {
		wordBreak: 'break-word',
		textAlign: 'left',
		'&::first-letter': {
			textTransform: 'capitalize',
		},
	},

	whiteSpacePreWrap: {
		whiteSpace: 'pre-wrap',
	},
	luminaFollowUpButtonStyleOverrides: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		minWidth: '0px',
		flex: '0 1 auto',
	},
});

export const FollowUpSuggestionFooter: ({
	omitDialogues,
}: {
	omitDialogues?: boolean;
}) => JSX.Element[] | null = ({ omitDialogues = false }: { omitDialogues?: boolean }) => {
	const {
		commonAttributes,
		fireAnalyticsEvent,
		setQuery,
		updateContextCache,
		onClose,
		closeParentDialog,
		callbacks,
		showChatFollowUps,
	} = useAIAnswerContext();
	const { followUps, query, answerString, searchSessionId, errorStateSuggestedQueries } =
		commonAttributes;
	const publish = usePublish('ai-mate');
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const isUXUplift = SAINFeatureControl.isSAINUXUpliftEnabled();
	const showNLPFollowUps = followUps.length > 0;

	const { serveExperiment: serveErrorStateSuggestedQueriesExp } = getFrontendExperiment({
		abTestId: 'kd_sain_show_no_answer_suggestions_exp',
	});

	const visibleFollowUps = useMemo(() => {
		return showNLPFollowUps
			? followUps.slice(0, 2)
			: serveErrorStateSuggestedQueriesExp
				? errorStateSuggestedQueries.slice(0, 2)
				: [];
	}, [showNLPFollowUps, followUps, errorStateSuggestedQueries, serveErrorStateSuggestedQueriesExp]);

	const showErrorStateSuggestedQueries = visibleFollowUps.length > 0 && !showNLPFollowUps;

	const onPillClick = useCallback(
		(followUp: string) => {
			if (showChatFollowUps) {
				publish({
					type: 'chat-new',
					source: 'sain',
					data: {
						name: followUp.substring(0, 30),
						dialogues: omitDialogues
							? []
							: [
									{
										human_message: { content: query },
										agent_message: { content: answerString },
									},
								],
						prompt: followUp,
						sourceId: searchSessionId,
					},
				});
			} else {
				setQuery(followUp);
				updateContextCache({ fromFollowUp: true });
			}

			fireAnalyticsEvent(
				onFollowUpSuggestionClick({
					...commonAttributes,
					followUpString: followUp,
					destination: 'chat',
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.FOLLOWUP_CLICKED,
				commonAttributes,
			});

			onClose?.();
			closeParentDialog?.();
			callbacks?.onFollowUpSuggestionClick?.();
		},
		[
			fireSAINUsageEvent,
			fireAnalyticsEvent,
			commonAttributes,
			onClose,
			closeParentDialog,
			callbacks,
			publish,
			query,
			answerString,
			searchSessionId,
			setQuery,
			updateContextCache,
			showChatFollowUps,
			omitDialogues,
		],
	);

	const onSuggestedQueryClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>, suggestedQuery: string) => {
			e.preventDefault();
			e.stopPropagation();

			setQuery(suggestedQuery);
			fireAnalyticsEvent(
				onErrorStateSuggestedQueryClick({
					...commonAttributes,
					errorStateSuggestedQuery: suggestedQuery,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.ERROR_STATE_SUGGESTED_QUERY_CLICKED,
				commonAttributes,
			});
			onClose?.();
			closeParentDialog?.();
		},
		[
			setQuery,
			fireAnalyticsEvent,
			fireSAINUsageEvent,
			onClose,
			closeParentDialog,
			commonAttributes,
		],
	);

	useEffect(() => {
		if (showErrorStateSuggestedQueries) {
			fireAnalyticsEvent(
				onErrorStateSuggestedQueriesShown({
					...commonAttributes,
					showErrorStateSuggestedQueries: visibleFollowUps,
				}),
			);
		}
	}, [showErrorStateSuggestedQueries, fireAnalyticsEvent, commonAttributes, visibleFollowUps]);

	if (isUXUplift && showChatFollowUps) {
		const openChatPill = (
			<Box xcss={styles.luminaFollowUpButtonStyleOverrides} key="open-chat">
				<OpenChatButton prompt={query} query={query} answerString={answerString} source="sain" />
			</Box>
		);

		if (visibleFollowUps.length > 0) {
			const followUp = visibleFollowUps[0];
			return [
				openChatPill,
				<Box xcss={styles.luminaFollowUpButtonStyleOverrides} key={followUp}>
					<Tooltip content={followUp} position="top">
						<Button
							iconBefore={RovoChatIcon}
							onClick={() => onPillClick(followUp)}
							testId="smart-answer-follow-up-button"
						>
							{followUp}
						</Button>
					</Tooltip>
				</Box>,
			];
		}

		return [openChatPill];
	}

	return visibleFollowUps.length > 0
		? visibleFollowUps.map((followUp, index) => {
				if (showChatFollowUps && !showErrorStateSuggestedQueries) {
					return (
						<Inline space="space.050" alignBlock="center" key={followUp}>
							<FollowUpButton
								onClick={() => onPillClick(followUp)}
								followUpText={followUp}
								followUpIndex={index}
								followUpType="UNAVAILABLE"
								experienceId="sain"
							/>
						</Inline>
					);
				}

				if (showErrorStateSuggestedQueries) {
					return (
						<Inline space="space.050" alignBlock="center" key={followUp}>
							<Pressable xcss={styles.button} onClick={(e) => onSuggestedQueryClick(e, followUp)}>
								<div css={stylesCompiled.pillLineHeight}>
									<Inline space="space.075" alignBlock="baseline">
										<SearchIcon color="currentColor" label="" />
										<Box xcss={cx(styles.queryText, styles.whiteSpacePreWrap)}>{followUp}</Box>
									</Inline>
								</div>
							</Pressable>
						</Inline>
					);
				}

				return (
					<Inline space="space.050" alignBlock="center" key={followUp}>
						<AIAnswerFollowUpPill onClick={() => onPillClick(followUp)}>
							<SearchIcon label="" color={token('color.icon')} />
							<AIAnswerFollowUpPillText>{followUp}</AIAnswerFollowUpPillText>
						</AIAnswerFollowUpPill>
					</Inline>
				);
			})
		: null;
};
